import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * App-root error boundary. One throw inside any screen would otherwise
 * blank the whole chrome (sidebar, topbar) — this catches it and shows
 * an analyst-legible fallback with a reset action.
 */
export default class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- boundary surface is the right place to log.
    console.error('[sentinel-ui] ErrorBoundary caught', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div role="alert" style={{ padding: 40, maxWidth: 720 }}>
          <div className="eyebrow" style={{ color: 'var(--red-600, #b91c1c)' }}>
            § SCREEN ERROR
          </div>
          <h1 className="serif" style={{ fontSize: 32, margin: '10px 0 0' }}>
            Something went wrong rendering this screen
          </h1>
          <p style={{ color: 'var(--slate-500)', fontSize: 14, marginTop: 12 }}>
            The rest of the app (nav, other tabs) is unaffected. The error was
            logged to the browser console for debugging.
          </p>
          <pre
            style={{
              background: 'var(--slate-50, #f8fafc)',
              border: '1px solid var(--slate-200, #e2e8f0)',
              padding: 12,
              marginTop: 16,
              fontSize: 12,
              overflow: 'auto',
            }}
          >
            {this.state.error.message}
          </pre>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: 16 }}
            onClick={this.reset}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
